import { jsx } from '@emotion/react';
import { FullStoryAPI } from '@zapier/react-fullstory';
import { formatTimeSpan, formatDateTime } from '@zapier/date';
import { Icon, Link, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { emitUserInteractionClick, InteractionGoal, InteractionName, } from '../../../../utils/avroEvents';
import { useUniversalNavContext } from '../../../../hooks';
import UsageBar from './UsageBar';
const Styles = labelStyles('UniversalSidebarUsage', {
    root: {
        display: 'grid',
        gap: 'var(--zds-space-12, 12px)',
        whiteSpace: 'nowrap',
    },
    header: {
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 'var(--zds-space-12, 12px)',
        alignItems: 'center',
        font: Typography.base.fontFamily,
        fontSize: 16,
        fontWeight: 600,
        color: 'var(--zds-text-default, #413735)',
        '& svg': {
            color: 'inherit',
        },
    },
    planName: {
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        font: `var(--zds-body-2, 500 14px/24px ${Typography.base.fontFamily})`,
    },
    usages: {
        display: 'grid',
        gap: 'var(--zds-space-8, 8px)',
        marginBottom: 'var(--zds-space-8, 8px)',
    },
    resetInfo: {
        display: 'grid',
        gap: 'var(--zds-space-8, 8px)',
        font: `var(--zds-minimal-print-1, 400 12px/20px ${Typography.base.fontFamily})`,
        gridTemplateRows: '1fr auto',
        paddingTop: '10px',
        color: 'var(--zds-text-weakest, #6F6765)',
        '> a': {
            color: 'var(--zds-text-link, #695BE8) !important',
            width: 'max-content',
            '&:hover': {
                color: 'var(--zds-text-link-hover, #503ebd) !important',
            },
            '&:focus-visible': {
                outlineColor: 'var(--zds-text-link, #695BE8) !important',
            },
            '&:focus': {
                outlineColor: 'none !important',
            },
        },
    },
});
const Usages = (props) => {
    const hasUsedExtraTasks = props.isPlanMetered && props.numTasks > props.numFreeTasksMax;
    return (jsx("div", { css: Styles.usages, "data-zds": true },
        jsx(UsageBar, { label: props.isPlanMetered ? 'Included Tasks' : 'Tasks', max: props.isPlanMetered ? props.numFreeTasksMax : props.numTasksMax, value: props.isPlanMetered
                ? Math.min(props.numTasks, props.numFreeTasksMax)
                : props.numTasks }),
        hasUsedExtraTasks && (jsx(UsageBar, { label: "Additional Tasks", max: props.numTasksMax - props.numFreeTasksMax, value: props.numTasks - props.numFreeTasksMax })),
        jsx(UsageBar, { label: "Zaps", max: props.numZapsMax, value: props.numZaps })));
};
const PlanUsage = ({ numTasks, numTasksMax, numZaps, numZapsMax, resetDate, children = undefined, trialPlanName = undefined, trialPlanEndDate = undefined, isPlanMetered = false, numFreeTasksMax = 0, managePlanHref = '/app/settings/billing', managePlanLabel = 'Manage plan', title = 'Usage', }) => {
    const { getFullyQualifiedURL } = useUniversalNavContext();
    const managePlanURL = getFullyQualifiedURL(managePlanHref);
    const relativeDate = formatTimeSpan(Date.now(), resetDate, false);
    const handleEmitAvroClickEvent = (event) => {
        event.preventDefault();
        FullStoryAPI('event', `Plan usage click from UniversalLayout sidebar`);
        emitUserInteractionClick(InteractionName.plan_usage_click, InteractionGoal.plan_usage, managePlanLabel, managePlanHref).finally(() => (location.href = managePlanURL || ''));
    };
    return (jsx("section", { css: Styles.root, "data-zds": true },
        jsx("div", { css: Styles.header, "data-zds": true },
            jsx(Icon, { name: "miscWallet", size: 20 }),
            jsx("h3", { css: Styles.planName, "data-zds": true }, trialPlanName ? `${trialPlanName} plan (Trial)` : title)),
        jsx("div", { "data-zds": true },
            jsx(Usages, { isPlanMetered,
                numFreeTasksMax,
                numTasks,
                numTasksMax,
                numZaps,
                numZapsMax }),
            jsx("p", { css: Styles.resetInfo, "data-zds": true },
                jsx("span", { "data-zds": true }, trialPlanName && trialPlanEndDate
                    ? `Trial ends on ${formatDateTime(trialPlanEndDate, 'default')}`
                    : `Usage resets in ${relativeDate}`),
                jsx(Link, { onClick: handleEmitAvroClickEvent, href: managePlanURL }, managePlanLabel))),
        children));
};
export default PlanUsage;
//# sourceMappingURL=PlanUsage.js.map
// @zapier/universal-layout@9.14.1 (2aac35652)
